/** Adapt a replayed payoff to the shape `PayoffChart` already draws.
 *
 * The chart was written for the live basket card and knows nothing about
 * the archive. Rather than fork it — two payoff charts drifting apart is
 * exactly the duplication this repo's gates exist to prevent — the
 * simulator's response is mapped onto its view type.
 *
 * The fields the chart does not use for drawing (`connected`, the leg
 * symbols) are filled with what is TRUE of a replay rather than with
 * placeholders: a replayed minute needs no broker session, so `connected`
 * is genuinely true, and the leg's identity is its strike and side.
 */

import type { PayoffView } from "@/lib/payoffApi";
import type { SimPayoff } from "@/lib/simulatorApi";

export function toPayoffView(payoff: SimPayoff): PayoffView {
  return {
    underlying: null,
    expiry: null,
    // A replay reads the archive, so there is nothing to be disconnected
    // from — the chart's "no live quotes" state would be a lie here.
    connected: true,
    spot: payoff.spot,
    spots: payoff.spots,
    expiryPnl: payoff.expiry_pnl,
    todayPnl: payoff.today_pnl,
    breakevens: payoff.breakevens,
    maxProfit: payoff.max_profit,
    maxLoss: payoff.max_loss,
    maxProfitUnbounded: payoff.max_profit_unbounded,
    maxLossUnbounded: payoff.max_loss_unbounded,
    netEntryCost: payoff.net_entry_cost,
    lastMtm: payoff.last_mtm,
    legs: payoff.legs.map((leg) => ({
      side: leg.direction,
      symbol: `${String(leg.strike)}${leg.side}`,
      strike: leg.strike,
      optionType: leg.side,
      quantity: leg.quantity,
      entryFill: leg.entry,
      ltp: leg.ltp,
    })),
  };
}
